import React from 'react';
import { Crown, Users, School, BookOpen, Wrench, Package, Bot, Github } from 'lucide-react';

export interface Product {
    id: string;
    tier: 'foundation' | 'learning' | 'community' | 'elite' | 'free';
    type: 'script' | 'course' | 'workshop' | 'membership' | 'bundle' | 'premium' | 'tool';
    name: string;
    description?: string;
    price: string; // Price is now always a string
    priceNote?: string;
    link: string;
    detailsLink?: string; // Optional link to details page
    extras?: string[];
    highlight?: boolean;
    soldOut?: boolean;
    limited?: boolean;
    limitedCount?: number;
    icon?: React.ElementType;
    themeColor?: string; // e.g., 'blue', 'orange', 'purple', 'green'
    featured?: boolean;
    bestSeller?: boolean;
    rating?: number;
    ratingCount?: number;
    discount?: {
        original: number | string;
        percentage: number;
    };
}

// --- Product Data ---

// TIER 4: ELITE LEVEL
export const OWN_THE_AGENT: Product = {
    id: 'premium-own-the-agent',
    tier: 'elite',
    type: 'premium',
    name: "Own the Agent™",
    price: "From €999/month",
    priceNote: "Limited to 15 members only",
    link: "/own",
    description: "Premium transformation & 1-on-1 consulting",
    extras: [
        "Very small group (max 15 members)",
        "1-on-1 calls with Mike",
        "Help with AI implementation for your business",
        "Priority access to ALL new products",
        "Direct email access",
        "Monthly strategy calls",
        "Exclusive workshops",
        "All scripts & workshops included",
        "Business transformation conversations",
        "Lifetime updates & support"
    ],
    highlight: true,
    limited: true,
    limitedCount: 15,
    icon: Crown,
    themeColor: 'indigo',
    featured: true,

    discount: {
        original: 1499,
        percentage: 33
    },
};

// TIER 3: COMMUNITY LEVEL
export const FROM_ADS_TO_AI: Product = {
    id: 'membership-ads-to-ai',
    tier: 'community',
    type: 'membership',
    name: "From Ads to AI™",
    price: "€199/mo or €499/yr",
    link: "/ai",
    description: "Ongoing AI learning & community support",
    extras: [
        "Monthly Masterclass Webinars",
        "Private Community Access",
        "1-on-1 Strategy Call (Founding Members)",
        "Weekly 'AI in Action' Videos",
        "PMax Asset Charts & Script Snippets",
        "Custom Prompt Library",
        "Priority Access to Beta Features"
    ],
    highlight: true,
    icon: Users,
    themeColor: 'purple'

};

// TIER 2: LEARNING LEVEL
export const BUILD_THE_AGENT: Product = {
    id: 'workshop-build-the-agent',
    tier: 'learning',
    type: 'workshop',
    name: "Build the Agent™",
    price: "€799", 
    link: "/build",
    description: "Transform Your Career with AI",
    extras: [
        "Small Group Live Training (via Zoom)",
        "Custom AI Agent included",
        "Learn to build tools like 8020agent",
        "Scripts & Sheets Mastery included",
        "Bonus scripts, templates & prompts",
        "Private community access",
        "Follow-up Q&A sessions",
        "Lifetime access to recordings",
        "Expert guidance and feedback",
        "Hands-on project development",
        "Career-transforming skills"
    ],
    soldOut: true,
    icon: School,
    themeColor: 'pink',
    bestSeller: true,
};

export const SCRIPTS_MASTERY: Product = {
    id: 'course-scripts-mastery',
    tier: 'learning',
    type: 'course',
    name: "Scripts & Sheets Mastery 2024",
    price: "€249",
    link: "https://mikerhodes.circle.so/checkout/2024-scripts-and-sheets-workshop",
    detailsLink: "/mastery",
    description: "Learn how to create your own automated tools",
    extras: [
        "Includes my QTS system for scripts",
        "Comprehensive training",
        "Learn in-demand skills",
        "Bonus scripts & templates included",
        "Lifetime access to updates"
    ],
    icon: BookOpen,
    themeColor: 'pink',
    rating: 4.7,
    ratingCount: 152,
};

// TIER 1: FOUNDATION LEVEL
export const MCC_PMAX_SCRIPT: Product = {
    id: 'script-mcc-pmax',
    tier: 'foundation',
    type: 'script',
    name: "MCC PMax Script",
    price: "$399", // Change to Dollar string
    link: "https://mikerhodes.circle.so/checkout/latest-mcc-script",
    description: "Use on unlimited accounts",
    extras: [
        "Create PMax sheet for all accounts",
        "Save hours of work",
        "Setup walkthrough video",
        "Includes all future updates",
        "Personal support from Mike included",
        "Include neg keyword script!"
    ],
    icon: Wrench,
    themeColor: 'blue',
    featured: false,
    rating: 4.9,
    ratingCount: 843,
    discount: {
        original: 499,
        percentage: 20
    },
    bestSeller: true
};

export const SINGLE_PMAX_SCRIPT: Product = {
    id: 'script-single-pmax',
    tier: 'foundation',
    type: 'script',
    name: "Single Account PMax Script",
    price: "$199", // Change to Dollar string
    link: "/pmax",
    description: "Used by 2000+ agencies",
    extras: [
        "Create PMax Insights in seconds",
        "Multiple reports included",
        "40 min video walkthrough",
        "Free support & updates"
    ],
    icon: Wrench,
    themeColor: 'blue',
    rating: 4.8,
    ratingCount: 321,
};

export const NEGATIVE_KEYWORD_SCRIPT: Product = {
    id: 'script-negative-keyword',
    tier: 'foundation',
    type: 'script',
    name: "Negative Keyword Script",
    price: "$109", // Change to Dollar string
    link: "https://mikerhodes.circle.so/checkout/negative-keyword-script",
    description: "Includes MCC version",
    extras: [
        "Works with Search campaigns",
        "Visualise your data in new ways",
        "Find redundant & conflicting negs",
        "Use AI to build lists, fast"
    ],
    icon: Wrench,
    themeColor: 'blue',
    rating: 4.6,
    ratingCount: 35,
};

// BUNDLE (Special)
export const MEGA_BUNDLE: Product = {
    id: 'bundle-mega',
    tier: 'foundation',
    type: 'bundle',
    name: "MASSIVE VALUE PACKAGE 🤪",
    price: "$499", // Change to Dollar string
    link: "https://mikerhodes.circle.so/checkout/massive-bundle",
    description: "Get all scripts and courses at a crazy discount",
    extras: [
        "MCC and PMax scripts",
        "Scripts & Sheets Mastery",
        "Negative Keyword Script",
        "Doesn't include Build the Agent™",
        "Get 9 bonus courses",
        "Priority support",
        "Save over $400",
        "Lifetime access to future updates"
    ],
    highlight: true,
    icon: Package,
    themeColor: 'blue',
    rating: 5,
    ratingCount: 78,
    discount: {
        original: 799,
        percentage: 38
    },
};

// IMPROVED BUNDLE (Derived)
export const IMPROVED_BUNDLE: Product = {
    ...MEGA_BUNDLE,
    id: 'bundle-improved-mega',
    name: "MASSIVE VALUE PACKAGE 🤪",
    price: "€799",
    description: "Get scripts, courses and community access at a discounted price",
    extras: [
        "From Ads to AI Membership (12 months) - value €499",
        "MCC and PMax scripts - value $598",
        "Scripts & Sheets Mastery - value €249",
        "Negative Keyword Script - value $109",
        "All 9 bonus courses - value €499",
        "Priority support",
        "Save over $1000",
        "Lifetime access to all script updates"
    ],
    discount: {
        original: 1954,
        percentage: 59
    },
    rating: 5,
    ratingCount: 78,
    highlight: true,
    themeColor: 'orange'
};

// --- FREE TOOLS ---
export const EIGHTY_TWENTY_AGENT: Product = {
    id: 'tool-8020agent',
    tier: 'free',
    type: 'tool',
    name: "80/20 Agent",
    price: "Free",
    link: "https://8020agent.com",
    description: "AI-powered analysis & insights for your Google Ads campaigns.",
    extras: [
        "Connect 5 Google Ads accounts",
        "Profitability analysis",
        "Optimization recommendations",
        "Time-saving insights",
        "Completely free to use"
    ],
    icon: Bot,
    themeColor: 'green',
};

export const FREE_SCRIPTS: Product = {
    id: 'tool-free-scripts',
    tier: 'free',
    type: 'tool',
    name: "Free Google Ads Scripts",
    price: "Free",
    link: "https://github.com/mikerhodesideas/free",
    description: "A collection of useful scripts to boost your productivity.",
    extras: [
        "Scripts to test API Keys",
        "PMax Search Terms Script",
        "Black Friday Dashboard",
        "PMax Placement Script",
        "N-Gram Analyzer",
        "Landing Page Script",
        "Explore more on GitHub"
    ],
    icon: Github,
    themeColor: 'green',
};

// --- Comparison Table Features Data START ---

// Feature interface
export interface Feature {
    id: string;
    name: string;
    description?: string;
    foundation: boolean | 'partial' | 'upgradable';
    learning: boolean | 'partial' | 'upgradable';
    community: boolean | 'partial' | 'upgradable';
    elite: boolean | 'partial' | 'upgradable';
    category: 'tools' | 'learning' | 'community' | 'support' | 'access';
}

// Features list
export const features: Feature[] = [
    // Tools & Resources (Reordered)
    {
        id: "neg-keyword-script",
        name: "Negative Keyword Script",
        description: "Ready-to-use script for negative keyword management",
        foundation: true,
        learning: 'partial',
        community: false,
        elite: true,
        category: "tools"
    },
    {
        id: "pmax-script-single",
        name: "PMax Script (Single Account)",
        description: "Ready-to-use script for optimizing PMax in a single account",
        foundation: true,
        learning: 'partial',
        community: false,
        elite: true,
        category: "tools"
    },
    {
        id: "pmax-script-mcc",
        name: "PMax Script (MCC)",
        description: "Ready-to-use script for optimizing PMax across an MCC",
        foundation: true,
        learning: 'partial',
        community: false,
        elite: true,
        category: "tools"
    },

    // Learning & Development
    {
        id: "script-mastery",
        name: "Scripts & Sheets Mastery",
        description: "Comprehensive course on creating automation tools",
        foundation: false,
        learning: true,
        community: false,
        elite: true,
        category: "learning"
    },
    {
        id: "build-agent",
        name: "Build the Agent™ Workshop",
        description: "Workshop on building custom AI agents for business",
        foundation: false,
        learning: true,
        community: false,
        elite: true,
        category: "learning"
    },
    {
        id: "monthly-masterclass",
        name: "Monthly Masterclasses",
        description: "Live workshops on advanced topics and new techniques",
        foundation: false,
        learning: false,
        community: true,
        elite: true,
        category: "learning"
    },

    // Community & Network
    {
        id: "private-community",
        name: "Private Community Access",
        description: "Connect with peers, ask questions, and share insights",
        foundation: false,
        learning: false,
        community: true,
        elite: true,
        category: "community"
    },
    {
        id: "networking",
        name: "Networking Opportunities",
        description: "Build connections with other Google Ads professionals",
        foundation: false,
        learning: false,
        community: true,
        elite: true,
        category: "community"
    },
    {
        id: "code-sharing",
        name: "Code Sharing & Templates",
        description: "Access a library of shared scripts and templates",
        foundation: false,
        learning: 'partial',
        community: true,
        elite: true,
        category: "community"
    },

    // Support & Guidance
    {
        id: "priority-support",
        name: "Priority Support",
        description: "Get faster responses to your questions and issues",
        foundation: false,
        learning: false,
        community: true,
        elite: true,
        category: "support"
    },
    {
        id: "direct-feedback",
        name: "Direct Feedback Channel",
        description: "Provide direct input on future product development",
        foundation: false,
        learning: false,
        community: true,
        elite: true,
        category: "support"
    },
    {
        id: "one-on-one",
        name: "1-on-1 Coaching Calls",
        description: "Personalized coaching sessions with Mike Rhodes",
        foundation: false,
        learning: false,
        community: false,
        elite: true,
        category: "support"
    },

    // Access & Updates
    {
        id: "lifetime-updates",
        name: "Lifetime Updates",
        description: "Receive all future updates for purchased products",
        foundation: true,
        learning: true,
        community: true,
        elite: true,
        category: "access"
    },
    {
        id: "early-access",
        name: "Early Access to Betas",
        description: "Get early access to new features and products",
        foundation: false,
        learning: false,
        community: true,
        elite: true,
        category: "access"
    },
    {
        id: "resource-library",
        name: "Exclusive Resource Library",
        description: "Access to a curated library of guides, checklists, and templates",
        foundation: false,
        learning: 'partial',
        community: true,
        elite: true,
        category: "access"
    }
];

// --- Comparison Table Features Data END --- 